import React, { useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router";
import { Home } from "lucide-react";
import {
  Breadcrumbs,
  type BreadcrumbItem,
  getCollapsedBreadcrumbsCustom,
} from "snap-design-system";
import { useFolderPath, useReportListActions } from "~/store/reportListStore";
import { REPORT_DETAIL_STORE_INSTANCE } from "~/store/details";
import { useFolderNavigation } from "~/hooks/useFolderNavigation";
import { CHECK_SESSION_INTERVAL, REPORT_CONSTANTS } from "~/helpers/constants";
import { useSessionGuard } from "~/hooks/useSessionGuard";
import { useNavigationGuard } from "~/hooks/useNavigationGuard";
import { useUnsavedChangesWarning } from "~/hooks/useUnsavedChangesWarning";
import { useFolderCRUD } from "~/hooks/useFolderCRUD";
import { HierarchicalFolderItem } from "~/types/global";

interface BreadcrumbNavigationProps {
  breadcrumbs?: BreadcrumbItem[];
  reportName?: string;
}

function findPathToFolder(
  folders: HierarchicalFolderItem[],
  targetId: string,
  path: { id: string; name: string }[] = []
): { id: string; name: string }[] | null {
  for (const folder of folders) {
    const currentPath = [...path, { id: folder.folder_id, name: typeof folder.folder_name === "string" ? folder.folder_name : folder.folder_id.slice(-8) }];
    if (folder.folder_id === targetId) return currentPath;
    if (folder.data?.length) {
      const found = findPathToFolder(folder.data, targetId, currentPath);
      if (found) return found;
    }
  }
  return null;
}

const BreadcrumbNavigation: React.FC<BreadcrumbNavigationProps> = ({
  breadcrumbs = [],
  reportName,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const location = useLocation();
  const { folderId } = useParams<{ folderId?: string }>();
  const folderPath = useFolderPath();
  const {
    selectors: { useReportMetadata },
  } = REPORT_DETAIL_STORE_INSTANCE;
  const reportMetadata = useReportMetadata();
  const { removeFoldersAfterIndex, addFolderToPath } = useReportListActions();
  const { navigateToFolder, navigateToRoot } = useFolderNavigation();
  const { checkSessionBeforeAction } = useSessionGuard({
    interval: CHECK_SESSION_INTERVAL[1],
  });
  const { shouldBlockNavigation, clearPendingChanges } = useNavigationGuard();
  const { showUnsavedChangesDialog } = useUnsavedChangesWarning({
    hasUnsavedChanges: shouldBlockNavigation,
    onConfirmNavigation: clearPendingChanges,
  });

  // Sync folderPath with URL on browser back/forward navigation
  useEffect(() => {
    if (!folderId || folderPath.length === 0) return;
    const currentIndex = folderPath.findIndex((f) => f.id === folderId);
    if (currentIndex !== -1 && currentIndex < folderPath.length - 1) {
      removeFoldersAfterIndex(currentIndex);
    }
  }, [folderId]);

  // Fetch hierarchical folders to rebuild path when folderPath is empty on direct URL access
  const needsPathRecovery = !!folderId && folderPath.length === 0;
  const { useHierarchicalFoldersQuery } = useFolderCRUD();
  const { data: hierarchicalFolders } = useHierarchicalFoldersQuery(undefined, {
    enabled: needsPathRecovery,
    staleTime: 60000,
  });

  const pathRecoveredRef = useRef(false);
  useEffect(() => {
    if (!needsPathRecovery || !hierarchicalFolders || !Array.isArray(hierarchicalFolders) || pathRecoveredRef.current) return;
    const path = findPathToFolder(hierarchicalFolders, folderId!);
    if (path) {
      pathRecoveredRef.current = true;
      path.forEach((folder) => addFolderToPath(folder));
    }
  }, [needsPathRecovery, hierarchicalFolders, folderId, addFolderToPath]);

  const handleNavigateToRoot = () => {
    if (shouldBlockNavigation) {
      showUnsavedChangesDialog(() => {
        navigateToRoot({ shouldInvalidateFilters: false });
      });
      return;
    }
    navigateToRoot({ shouldInvalidateFilters: false });
  };

  const isReportListPage = location.pathname === "/" || location.pathname.startsWith("/pasta/");
  const isReportDetailsPage = location.pathname.startsWith("/report/");

  const homeLink: BreadcrumbItem = {
    title: "dashboard",
    icon: <Home />,
    onClick: handleNavigateToRoot,
  };

  const handleNavigateToFolder = (targetFolderId: string, index: number) => {
    if (shouldBlockNavigation) {
      showUnsavedChangesDialog(() => {
        checkSessionBeforeAction();
        if (index < folderPath.length - 1) {
          removeFoldersAfterIndex(index);
        }
        navigateToFolder(targetFolderId);
      });
      return;
    }

    checkSessionBeforeAction();
    if (index < folderPath.length - 1) {
      removeFoldersAfterIndex(index);
    }
    navigateToFolder(targetFolderId);
  };

  const buildBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbItems: BreadcrumbItem[] = [homeLink];

    if (isReportListPage) {
      folderPath.forEach((folder, index) => {
        breadcrumbItems.push({
          title: folder.name,
          onClick: () => handleNavigateToFolder(folder.id, index),
        });
      });
    } else if (isReportDetailsPage && reportMetadata) {
      const parentFolderId = reportMetadata[REPORT_CONSTANTS.new_report.parent_folder_id];
      const reportName =
        (reportMetadata[REPORT_CONSTANTS.new_report.report_name] as string) ||
        (reportMetadata[REPORT_CONSTANTS.new_report.subject_name] as string);
      const folderName = reportMetadata[REPORT_CONSTANTS.new_report.folder_name] as string;

      if (folderPath.length > 0) {
        folderPath.forEach((folder, index) => {
          breadcrumbItems.push({
            title: folder.name,
            onClick: () => handleNavigateToFolder(folder.id, index),
          });
        });
      } else if (parentFolderId && folderName) {
        breadcrumbItems.push({
          title: folderName,
          onClick: () => {
            if (shouldBlockNavigation) {
              showUnsavedChangesDialog(() => {
                navigateToFolder(parentFolderId as string);
              });
              return;
            }
            navigateToFolder(parentFolderId as string);
          },
        });
      }

      if (reportName) {
        breadcrumbItems.push({
          title: reportName,
        });
      }
    } else {
      breadcrumbItems.push(...breadcrumbs);
    }

    return breadcrumbItems;
  };

  const defaultBreadcrumbs = buildBreadcrumbs();
  const collapsedBreadcrumbs = getCollapsedBreadcrumbsCustom(defaultBreadcrumbs, 3);

  return (
    <Breadcrumbs
      uppercase
      items={collapsedBreadcrumbs}
      onCollapsedMenuOpenChange={setIsDropdownOpen}
      showFullPath
    />
  );
};

export default BreadcrumbNavigation;
